import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CalendarDays, ChevronLeft, ChevronRight, Edit3, Plus,
  Trash2, Download, ClipboardList, CheckCircle2, Clock,
  FileText, Settings, X, Copy,
} from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const STATUS_OPTIONS = [
  { value: "done", label: "Done" },
  { value: "in_progress", label: "In Progress" },
  { value: "skipped", label: "Skipped" },
  { value: "pending", label: "Pending" },
];

/* ---------- Helpers ---------- */
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const snapMon = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
  const wd = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - wd);
  return toISO(d);
};
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
  day: "2-digit", month: "short",
});

const statusPill = (s) => {
  if (s === "done") return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (s === "in_progress") return "bg-blue-100 text-blue-800 border-blue-300";
  if (s === "skipped") return "bg-slate-200 text-slate-700 border-slate-300";
  return "bg-amber-100 text-amber-800 border-amber-300";
};

/**
 * Shared component used by both admin (/admin/sm-checklist) and SM portal
 * (/my-portal/sm-checklist). When `forSelf` is true, SM list is hidden and
 * grid auto-loads for the logged-in user.
 */
export default function SMChecklist({ forSelf = false }) {
  const { user } = useAuth();
  const myId = user?.id;
  const isAdmin = ["super_admin", "mis"].includes(user?.role);
  const isEA = user?.role === "ea";
  const canManageTemplates = isAdmin || isEA || user?.role === "senior_management";

  const [smList, setSmList] = useState([]);
  const [selectedSm, setSelectedSm] = useState(forSelf ? myId : "");
  const [weekStart, setWeekStart] = useState(snapMon(""));
  const weekEnd = useMemo(() => addDays(weekStart, 5), [weekStart]);

  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);

  // Log dialog
  const [logDlg, setLogDlg] = useState(null);
  // logDlg = { task: {task_id, task_name, category}, day_index, log_date, existing }
  const [logForm, setLogForm] = useState({
    start_time: "", end_time: "", status: "done", remarks: "",
  });
  const [savingLog, setSavingLog] = useState(false);

  // Templates modal
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState({
    category: "", task_name: "", scope: "common", sort_order: 100,
  });
  const [editingTpl, setEditingTpl] = useState(null);

  /* ---------- Loaders ---------- */
  const loadSmList = useCallback(async () => {
    try {
      const { data } = await api.get("/sm-checklist/sm-list");
      setSmList(data || []);
      if (!forSelf && !selectedSm && data && data.length > 0) {
        setSelectedSm(data[0].id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load SM list");
    }
  }, [forSelf, selectedSm]);

  const loadGrid = useCallback(async () => {
    if (!selectedSm) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/sm-checklist/weekly?sm_id=${selectedSm}&week_start=${weekStart}`,
      );
      setGrid(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load grid");
      setGrid(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSm, weekStart]);

  const loadTemplates = useCallback(async () => {
    if (!selectedSm) return;
    try {
      const { data } = await api.get(`/sm-checklist/templates?sm_id=${selectedSm}`);
      setTemplates(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load templates");
    }
  }, [selectedSm]);

  useEffect(() => {
    loadSmList();
  }, [loadSmList]);

  useEffect(() => {
    if (selectedSm) loadGrid();
  }, [selectedSm, weekStart, loadGrid]);

  /* ---------- Log dialog handlers ---------- */
  const openLogDialog = (task, dayIdx) => {
    const logDate = grid.days[dayIdx];
    const existing = task.logs_by_day[dayIdx];
    setLogDlg({ task, day_index: dayIdx, log_date: logDate, existing });
    setLogForm({
      start_time: existing?.start_time || "",
      end_time: existing?.end_time || "",
      status: existing?.status || "done",
      remarks: existing?.remarks || "",
    });
  };

  const saveLog = async () => {
    if (!logDlg) return;
    setSavingLog(true);
    try {
      await api.post("/sm-checklist/log", {
        sm_id: selectedSm,
        task_id: logDlg.task.task_id,
        log_date: logDlg.log_date,
        start_time: logForm.start_time || null,
        end_time: logForm.end_time || null,
        status: logForm.status,
        remarks: logForm.remarks,
      });
      toast.success("Log saved");
      setLogDlg(null);
      loadGrid();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSavingLog(false);
    }
  };

  const deleteLog = async () => {
    if (!logDlg?.existing?.id) return;
    if (!window.confirm("Delete this log entry?")) return;
    setSavingLog(true);
    try {
      await api.delete(`/sm-checklist/log/${logDlg.existing.id}`);
      toast.success("Log deleted");
      setLogDlg(null);
      loadGrid();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setSavingLog(false);
    }
  };

  /* ---------- Templates handlers ---------- */
  const openTemplates = () => {
    loadTemplates();
    setTplOpen(true);
    setEditingTpl(null);
    setTplForm({
      category: "", task_name: "", scope: "common", sort_order: 100,
    });
  };

  const startEdit = (t) => {
    setEditingTpl(t);
    setTplForm({
      category: t.category,
      task_name: t.task_name,
      scope: t.scope,
      sm_id: t.sm_id,
      sort_order: t.sort_order || 100,
    });
  };

  const saveTpl = async () => {
    if (!tplForm.category.trim() || !tplForm.task_name.trim()) {
      toast.error("Category & Task name required");
      return;
    }
    try {
      const payload = { ...tplForm };
      if (payload.scope === "sm" && !payload.sm_id) payload.sm_id = selectedSm;
      if (editingTpl) {
        await api.patch(`/sm-checklist/templates/${editingTpl.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/sm-checklist/templates", payload);
        toast.success("Template created");
      }
      setEditingTpl(null);
      setTplForm({ category: "", task_name: "", scope: "common", sort_order: 100 });
      loadTemplates();
      loadGrid();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const deleteTpl = async (t) => {
    if (!window.confirm(`Remove task "${t.task_name}"? (Existing logs are preserved.)`)) return;
    try {
      await api.delete(`/sm-checklist/templates/${t.id}`);
      toast.success("Task removed");
      loadTemplates();
      loadGrid();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadPdf = () => {
    if (!selectedSm) return;
    const url = `${API_BASE}/sm-checklist/weekly/pdf?sm_id=${selectedSm}&week_start=${weekStart}`;
    window.open(url, "_blank");
  };

  const [dupBusy, setDupBusy] = useState(false);
  const duplicatePrevWeek = async () => {
    if (!selectedSm) return;
    const prevMon = addDays(weekStart, -7);
    if (!window.confirm(
      `Copy all logs from week of ${fmtDate(prevMon)} into ${fmtDate(weekStart)}?\n\n` +
      `Existing logs in target week will be kept (this only fills empty slots).`,
    )) return;
    setDupBusy(true);
    try {
      const { data } = await api.post("/sm-checklist/duplicate-week", {
        sm_id: selectedSm,
        source_week_start: prevMon,
        target_week_start: weekStart,
        overwrite_existing: false,
      });
      if (data.copied === 0) {
        toast.info(data.message || "Nothing to copy");
      } else {
        toast.success(`Copied ${data.copied} log${data.copied === 1 ? "" : "s"}` +
          (data.skipped ? `, skipped ${data.skipped} (already filled)` : ""));
        loadGrid();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Copy failed");
    } finally {
      setDupBusy(false);
    }
  };

  const canEditCell = useMemo(() => {
    if (!user) return false;
    if (isAdmin) return true;
    if (user.role === "senior_management") return selectedSm === myId;
    if (isEA) {
      // EA can edit logs for their principals (server enforces too)
      return true;
    }
    return false;
  }, [user, isAdmin, isEA, myId, selectedSm]);

  /* ---------- Group rows by category for rendering ---------- */
  const groupedRows = useMemo(() => {
    if (!grid) return [];
    const out = [];
    let lastCat = null;
    grid.grid.forEach((row) => {
      if (row.category !== lastCat) {
        out.push({ isHeader: true, category: row.category });
        lastCat = row.category;
      }
      out.push({ isHeader: false, ...row });
    });
    return out;
  }, [grid]);

  return (
    <div className="space-y-4" data-testid="sm-checklist-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <ClipboardList className="h-3 w-3" /> Senior Management
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
            Weekly Checklist
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Daily TO-DO grid by category. EA/SM log start &amp; end time per task per day.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEditCell && selectedSm && (
            <Button variant="outline" onClick={duplicatePrevWeek} disabled={dupBusy} data-testid="duplicate-week-btn">
              <Copy className="h-4 w-4 mr-1.5" /> {dupBusy ? "Copying…" : "Copy Last Week"}
            </Button>
          )}
          {canManageTemplates && (
            <Button variant="outline" onClick={openTemplates} data-testid="manage-templates-btn">
              <Settings className="h-4 w-4 mr-1.5" /> Manage Tasks
            </Button>
          )}
          {selectedSm && (
            <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          )}
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          {!forSelf && (
            <>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Senior Mgmt
              </Label>
              <Select value={selectedSm} onValueChange={setSelectedSm}>
                <SelectTrigger className="h-8 w-56 text-xs" data-testid="sm-picker">
                  <SelectValue placeholder="Select SM…" />
                </SelectTrigger>
                <SelectContent>
                  {smList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="w-px h-5 bg-slate-300" />
            </>
          )}
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3" /> Week
          </Label>
          <Button size="sm" variant="outline"
            onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Input type="date" value={weekStart}
            onChange={(e) => setWeekStart(snapMon(e.target.value))}
            className="h-8 w-36 text-xs" data-testid="week-date" />
          <div className="text-xs text-slate-600">
            <b className="text-slate-900">{fmtDate(weekStart)}</b>
            {" → "}<b className="text-slate-900">{fmtDate(weekEnd)}</b>
            <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase">Mon → Sat</span>
          </div>
          <Button size="sm" variant="outline"
            onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline"
            onClick={() => setWeekStart(snapMon(""))} data-testid="this-week">
            This Week
          </Button>
          {grid && (
            <div className="ml-auto text-xs text-slate-500" data-testid="grid-stats">
              <CheckCircle2 className="h-3 w-3 inline text-emerald-600 mr-1" />
              {grid.stats.completed} / {grid.stats.total_slots} done
              <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold tabular-nums">
                {grid.stats.compliance_pct}%
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grid */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="px-3 py-8 text-center text-slate-400 text-sm">Loading…</div>
          )}
          {!loading && grid && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-blue-50 border-b-2 border-blue-200">
                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-700 font-bold w-[40%] min-w-[260px]">
                      TO DO
                    </th>
                    {DAYS.map((d, i) => (
                      <th key={d} className="px-2 py-2 text-center text-[11px] uppercase tracking-wider text-slate-700 font-bold border-l border-blue-200 min-w-[100px]">
                        <div>{d}</div>
                        <div className="text-[10px] text-slate-500 font-normal mt-0.5">
                          {fmtDate(grid.days[i])}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedRows.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                      No tasks configured. Click <b>Manage Tasks</b> to add.
                    </td></tr>
                  )}
                  {groupedRows.map((row, idx) => {
                    if (row.isHeader) {
                      return (
                        <tr key={`h-${idx}`} className="bg-slate-50">
                          <td colSpan={7} className="px-3 py-1.5 font-bold text-slate-800 text-xs uppercase tracking-wider border-t border-slate-200">
                            {row.category}
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={row.task_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`task-row-${row.task_id}`}>
                        <td className="px-3 py-2 text-slate-800 align-top">
                          {row.task_name}
                          {row.scope === "sm" && (
                            <span className="ml-1 inline-block text-[9px] uppercase bg-purple-100 text-purple-800 px-1 rounded">custom</span>
                          )}
                        </td>
                        {row.logs_by_day.map((log, i) => (
                          <td key={i}
                            className={`px-1 py-1 text-center align-top border-l border-slate-100 ${canEditCell ? "cursor-pointer hover:bg-blue-50" : ""}`}
                            onClick={() => canEditCell && openLogDialog(row, i)}
                            data-testid={`cell-${row.task_id}-${i}`}>
                            {log ? (
                              <div className={`inline-block min-w-[80px] px-1.5 py-1 rounded border text-[10px] ${statusPill(log.status)}`}>
                                {log.start_time && (
                                  <div className="font-semibold tabular-nums">
                                    {log.start_time}{log.end_time ? `→${log.end_time}` : ""}
                                  </div>
                                )}
                                {log.status !== "done" && (
                                  <div className="uppercase text-[9px]">{log.status.replace("_", " ")}</div>
                                )}
                                {log.remarks && (
                                  <div className="text-[9px] italic truncate max-w-[100px]" title={log.remarks}>
                                    {log.remarks}
                                  </div>
                                )}
                              </div>
                            ) : (
                              canEditCell && (
                                <span className="text-slate-300 hover:text-slate-500 text-xs">+</span>
                              )
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ----- Log Cell Dialog ----- */}
      <Dialog open={!!logDlg} onOpenChange={(v) => !v && setLogDlg(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="log-dialog">
          <DialogHeader>
            <DialogTitle>Log Task Activity</DialogTitle>
            <DialogDescription className="sr-only">
              Record start time, end time, status and remarks for this task on the selected day.
            </DialogDescription>
          </DialogHeader>
          {logDlg && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {logDlg.task.category} · {fmtDate(logDlg.log_date)}
                </div>
                <div className="font-medium text-slate-900 mt-0.5">{logDlg.task.task_name}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase text-slate-500">Start Time</Label>
                  <Input type="time" value={logForm.start_time}
                    onChange={(e) => setLogForm({ ...logForm, start_time: e.target.value })}
                    className="mt-1 h-8 text-xs" data-testid="start-time" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase text-slate-500">End Time</Label>
                  <Input type="time" value={logForm.end_time}
                    onChange={(e) => setLogForm({ ...logForm, end_time: e.target.value })}
                    className="mt-1 h-8 text-xs" data-testid="end-time" />
                </div>
              </div>
              {logForm.start_time && logForm.end_time && (
                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Duration: {(() => {
                    const [sh, sm] = logForm.start_time.split(":").map(Number);
                    const [eh, em] = logForm.end_time.split(":").map(Number);
                    const mins = (eh * 60 + em) - (sh * 60 + sm);
                    if (mins <= 0) return "—";
                    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
                  })()}
                </div>
              )}
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Status</Label>
                <Select value={logForm.status} onValueChange={(v) => setLogForm({ ...logForm, status: v })}>
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase text-slate-500">Remarks</Label>
                <Textarea rows={2} value={logForm.remarks}
                  onChange={(e) => setLogForm({ ...logForm, remarks: e.target.value })}
                  placeholder="Optional notes"
                  className="mt-1 text-xs" data-testid="log-remarks" />
              </div>
            </div>
          )}
          <DialogFooter>
            {logDlg?.existing && (
              <Button variant="outline" onClick={deleteLog} disabled={savingLog}
                className="text-red-700 border-red-200 hover:bg-red-50 mr-auto" data-testid="delete-log-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setLogDlg(null)} disabled={savingLog}>Cancel</Button>
            <Button onClick={saveLog} disabled={savingLog}
              className="brand-bg brand-bg-hover text-white" data-testid="save-log-btn">
              {savingLog ? "Saving…" : "Save Log"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Templates Manager Dialog ----- */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-3xl bg-white" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle>Manage Tasks Templates</DialogTitle>
            <DialogDescription className="sr-only">
              Add, edit or remove task templates. Common tasks apply to all SMs; SM-specific tasks apply only to the selected SM.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {/* Form */}
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase text-slate-500">Category</Label>
                    <Input value={tplForm.category}
                      onChange={(e) => setTplForm({ ...tplForm, category: e.target.value })}
                      placeholder="e.g. CRR"
                      className="h-8 mt-1 text-xs" data-testid="tpl-category" />
                  </div>
                  <div className="col-span-6">
                    <Label className="text-[10px] uppercase text-slate-500">Task Name</Label>
                    <Input value={tplForm.task_name}
                      onChange={(e) => setTplForm({ ...tplForm, task_name: e.target.value })}
                      placeholder="Task description"
                      className="h-8 mt-1 text-xs" data-testid="tpl-name" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">Scope</Label>
                    <Select value={tplForm.scope} onValueChange={(v) => setTplForm({ ...tplForm, scope: v })}>
                      <SelectTrigger className="h-8 mt-1 text-xs" data-testid="tpl-scope">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="common">Common (all SMs)</SelectItem>
                        <SelectItem value="sm">This SM only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px] uppercase text-slate-500">Order</Label>
                    <Input type="number" value={tplForm.sort_order}
                      onChange={(e) => setTplForm({ ...tplForm, sort_order: parseInt(e.target.value) || 100 })}
                      className="h-8 mt-1 text-xs" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  {editingTpl && (
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditingTpl(null);
                      setTplForm({ category: "", task_name: "", scope: "common", sort_order: 100 });
                    }}>
                      <X className="h-3 w-3 mr-1" />Cancel Edit
                    </Button>
                  )}
                  <Button size="sm" onClick={saveTpl} className="brand-bg brand-bg-hover text-white" data-testid="save-tpl-btn">
                    {editingTpl ? (<><Edit3 className="h-3 w-3 mr-1" />Update</>) : (<><Plus className="h-3 w-3 mr-1" />Add Task</>)}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* List */}
            <div className="border border-slate-200 rounded max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-2 py-1.5">Category</th>
                    <th className="px-2 py-1.5">Task</th>
                    <th className="px-2 py-1.5">Scope</th>
                    <th className="px-2 py-1.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-t border-slate-100" data-testid={`tpl-row-${t.id}`}>
                      <td className="px-2 py-1.5 text-xs font-medium text-slate-700">{t.category}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-800">{t.task_name}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {t.scope === "common"
                          ? <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[10px]">All SMs</span>
                          : <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-[10px]">{t.sm_name || "This SM"}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => startEdit(t)} data-testid={`edit-tpl-${t.id}`}>
                          <Edit3 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-600 hover:bg-red-50" onClick={() => deleteTpl(t)} data-testid={`del-tpl-${t.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {templates.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs">
                      No tasks yet. Add one above.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
